import {
  getControllerCandidateResumeSubmission
} from '../../../components/Candidates/ResumeSubmission/CandidateResumeSubmission';
import {
  KEY__CREDENTIAL__PRIMARY,
  KEY__CREDENTIAL__SECONDARY
} from '../../../dictionaries/Credential.dic';
import {
  Arr
} from '../../Array.lib';
import {
  NOT
} from '../../Boolean.lib';
import Core from '../../Core';
import {
  EMPLOYER_SUBMISSION_METHOD__ATS,
  ID__ATS_TYPE__ASHBY_API
} from '../../Definition';
import Employer from '../../Employer';
import globalErrorHandler from '../../Error/globalErrorHandler.fun';
import {
  capitalize
} from '../../GenericTools.lib';
import {
  joinHTML
} from '../../HTML.lib';
import Http from '../../Http';
import {
  Obj,
  deepMerge,
  getPersonName
} from '../../Object.lib';
import {
  Str,
  trim
} from '../../String.lib';
import encodeBasicAuthCredentials from '../../String/encodeBasicAuthCredentials.fun';
import {
  COLLECTION__EMPLOYERS,
  readLoopbackRecord
} from '../BE/loopback.api';
import {
  sendSafeEmail
} from '../Email/Email.lib';
import getAtsContextProperty from './getAtsContextProperty.fun';

export const KEY__PROVIDER__ASHBY = 'ashby';

const Ashby = {
  openEmployerSettings: ({ employerId }) => {
    Core.openPopUp(
      Core.getPath(`employer/settings/${employerId}`),
      1600
    );
  },
  getAtsContext: async ({ employerId }) => {
    try {
      return await readLoopbackRecord({
        collection: COLLECTION__EMPLOYERS,
        where: { id: employerId },
        fields: ['atsContext'],
        limit: 1,
        mapper: ({ atsContext }) => Obj(Obj(atsContext)[ID__ATS_TYPE__ASHBY_API])
      });
    }
    catch (error) {
      globalErrorHandler(error);
    }
  },
  isAtsAshbyApiSelected: async ({ employerId }) => {
    try {
      return await readLoopbackRecord({
        collection: COLLECTION__EMPLOYERS,
        where: { id: employerId },
        fields: ['atsTypeId'],
        limit: 1,
        mapper: ({ atsTypeId }) => atsTypeId === ID__ATS_TYPE__ASHBY_API
      });
    }
    catch (error) {
      globalErrorHandler(error);
    }
  },
  updateAtsContext: async ({ employerId, update }) => {
    try {
      const atsContext = deepMerge(
        await readLoopbackRecord({
          collection: COLLECTION__EMPLOYERS,
          where: { id: employerId },
          fields: ['atsContext'],
          limit: 1,
          mapper: ({ atsContext }) => atsContext
        }),
        { [ID__ATS_TYPE__ASHBY_API]: update }
      );
      await Employer.update(employerId, { atsContext });
      return atsContext[ID__ATS_TYPE__ASHBY_API];
    }
    catch (error) {
      globalErrorHandler(error);
    }
  },
  getSources: async ({
    apiKey = ''
  }) => {
    try {
      const requestOptions = {
        provider: KEY__PROVIDER__ASHBY,
        action: 'getSources',
        context: {
          INCLUDE_DEACTIVATED: false,
          API_KEY: encodeBasicAuthCredentials({ username: apiKey }),
        }
      };
      return Arr(
        Obj(
          await Http.post(Core.getApi('Credentials/api'), requestOptions)
        ).results
      );
    }
    catch (error) {
      globalErrorHandler(error);
    }
  },
  getUsers: async (options = {}) => {
    try {
      const requestOptions = {
        provider: KEY__PROVIDER__ASHBY,
        action: 'getUsers',
        context: {
          API_KEY: encodeBasicAuthCredentials({ username: options.apiKey }),
        },
        body: {
          cursor: options.cursor,
          limit: options.limit,
          syncToken: options.syncToken,
          includeDeactivated: options.includeDeactivated
        }
      };
      const response = Obj(
        await Http.post(Core.getApi('Credentials/api'), requestOptions)
      );
      const results = Arr(response.results);
      if (response.moreDataAvailable === true) {
        options.cursor = results.nextCursor;
        results.push(...await Ashby.getUsers(options));
      }
      return results;
    }
    catch (error) {
      globalErrorHandler(error);
    }
  },
  searchUser: async ({
    userEmail = '',
    apiKey = ''
  }) => {
    try {
      const requestOptions = {
        provider: KEY__PROVIDER__ASHBY,
        action: 'searchUser',
        context: {
          API_KEY: encodeBasicAuthCredentials({ username: apiKey }),
          USER__EMAIL: userEmail
        }
      };
      const result = Obj(
        Arr(
          Obj(
            await Http.post(Core.getApi('Credentials/api'), requestOptions)
          ).results
        )[0]
      );
      if (!result.id) {
        Core.showWarning(`Ashby - Unable to locate ${process.env.REACT_APP_TEAM_EMAIL} user`);
      }
      return result;
    }
    catch (error) {
      globalErrorHandler(error);
    }
  },
  getJob: async ({
    id: ASHBY__JOB__ID,
    apiKey: API_KEY,
  }) => {
    const result = {};
    try {
      const requestOptions = {
        provider: KEY__PROVIDER__ASHBY,
        action: 'getJob',
        context: {
          API_KEY,
          ASHBY__JOB__ID
        }
      };
      result.job = Obj(
        await Http.post(Core.getApi('Credentials/api'), requestOptions)
      ).results;
    }
    catch (error) {
      globalErrorHandler(error);
    }
    return Obj(result.job);
  },
  getInterviewStages: async ({
    interviewPlanId: ASHBY__INTERVIEW_PLAN__ID,
    apiKey: API_KEY
  }) => {
    const result = {};
    try {
      const requestOptions = {
        provider: KEY__PROVIDER__ASHBY,
        action: 'getInterviewStages',
        context: {
          API_KEY,
          ASHBY__INTERVIEW_PLAN__ID
        }
      };
      result.list = Obj(
        await Http.post(Core.getApi('Credentials/api'), requestOptions)
      ).results;
    }
    catch (error) {
      globalErrorHandler(error);
    }
    return Arr(result.list);
  },
  submit: async ({
    candidate = {},
    job = {},
    emailParams = {},
    submissionNotes = '',
  } = {}) => {
    const CAPITALIZED__PROVIDER = capitalize(KEY__PROVIDER__ASHBY)
    const results = {};
    try {
      const { employerId, employer = {} } = job;
      const { resumes = [] } = candidate;
      const resume = resumes[0];

      const ID__ASHBY__JOB = getAtsContextProperty({
        entity: job,
        atsTypeId: ID__ATS_TYPE__ASHBY_API,
        property: 'jobId'
      }).trim();

      if (NOT(ID__ASHBY__JOB)) {
        Core.showError(
          `10x10 - Missing Ashby Job ID in configuration`
        );
        return;
      }

      const ID__ASHBY__SOURCE = getAtsContextProperty({
        entity: employer,
        atsTypeId: ID__ATS_TYPE__ASHBY_API,
        property: 'sourceId'
      }).trim();

      const ID__ASHBY__USER = getAtsContextProperty({
        entity: employer,
        atsTypeId: ID__ATS_TYPE__ASHBY_API,
        property: 'userId'
      }).trim();

      const API_KEY = encodeBasicAuthCredentials({
        username: getAtsContextProperty({
          entity: employer,
          atsTypeId: ID__ATS_TYPE__ASHBY_API,
          property: 'apiKey'
        }).trim()
      });

      // CREATE ASH BY CANDIDATE
      Core.showLoader(
        `${CAPITALIZED__PROVIDER} - Submitting candidate`
      );
      const ashbyCandidate = Obj(
        Obj(
          await Http.post(Core.getApi('Credentials/api'), {
            provider: KEY__PROVIDER__ASHBY,
            employerId,
            action: 'submitCandidate',
            context: {
              CANDIDATE__NAME: getPersonName(candidate),
              CANDIDATE__EMAIL: Str(candidate.email),
              ASHBY__SOURCE__ID: ID__ASHBY__SOURCE,
              ASHBY__USER__ID: ID__ASHBY__USER,
              API_KEY,
            },
            body: {
              phoneNumber: trim(candidate.phone),
              linkedInUrl: trim(candidate.linkedInURL),
              githubUrl: trim(candidate.gitHubURL),
            }
          })
        ).results
      );
      const ID__ASHBY__CANDIDATE = trim(ashbyCandidate.id);
      if (!ID__ASHBY__CANDIDATE) {
        const error = `Ashby - Candidate submission failed`;
        results.errors = Arr(results.errors);
        results.errors.unshift(error);
        // ❌ Escape process if there is an error submitting candidate
        throw error;
      }

      // CREATE ASH BY CANDIDATE NOTE
      try {
        Core.showLoader(
          `${CAPITALIZED__PROVIDER} - Posting submission notes`
        );
        await Http.post(Core.getApi('Credentials/api'), {
          provider: KEY__PROVIDER__ASHBY,
          action: 'createNote',
          context: {
            ASHBY__CANDIDATE__ID: ID__ASHBY__CANDIDATE,
            // BB prefers using HTML formatting for notes in Ashby submissions
            CANDIDATE__NOTE: joinHTML(emailParams.html),
            API_KEY
          }
        });
      }
      catch (error) {
        const _error = `Ashby - Note submission failed`;
        results.errors = Arr(results.errors);
        results.errors.unshift(_error);
        // ⚠️ Continue process even if there is an error sending notes
      }

      // UPLOAD CANDIDATE RESUME TO ASH BY
      try {
        if (resume && resume.url && resume.filename) {
          Core.showLoader(
            `${CAPITALIZED__PROVIDER} - Uploading resume`
          );
          await Http.post(Core.getApi('Credentials/api'), {
            provider: KEY__PROVIDER__ASHBY,
            action: 'uploadResume',
            context: {
              ASHBY__CANDIDATE__ID: ID__ASHBY__CANDIDATE,
              RESUME__FILENAME: Str(resume.filename),
              RESUME__URL: trim(resume.url),
              API_KEY
            }
          });
        }
      }
      catch (error) {
        const _error = `Ashby - Resume upload failed`;
        results.errors = Arr(results.errors);
        results.errors.unshift(_error);
        console.error(_error, error);
        // ⚠️ Continue process even if there is an error uploading resume
      }

      // CREATE ASH BY APPLICATION
      Core.showLoader(
        `${CAPITALIZED__PROVIDER} - Creating application`
      );
      // Get job info
      const ashbyJob = Obj(
        await Ashby.getJob({
          id: ID__ASHBY__JOB,
          apiKey: API_KEY
        })
      );
      if (NOT(trim(ashbyJob.id))) {
        const error = `Ashby - Job ID not found`;
        results.errors = Arr(results.errors);
        results.errors.unshift(error);
        // ❌ Escape process if there is an error getting job info
        throw error;
      }
      // Get default interview plan
      const ID__ASHBY__INTERVIEW_PLAN = trim(
        ashbyJob.defaultInterviewPlanId
      );
      if (NOT(ID__ASHBY__INTERVIEW_PLAN)) {
        const error = `Ashby - Default Interview Plan ID in Job not found`;
        results.errors = Arr(results.errors);
        results.errors.unshift(error);
        // ❌ Escape process if there is an error getting default interview plan
        throw error;
      }
      // Get interview stages
      const ashbyStages = Arr(
        await Ashby.getInterviewStages({
          interviewPlanId: ID__ASHBY__INTERVIEW_PLAN,
          apiKey: API_KEY
        })
      );
      // Get review stage
      const ID__ASHBY__INTERVIEW_STAGE = Str(
        Obj(
          ashbyStages.find((stage) => /review/i.test(stage.title))
        ).id
      );
      if (NOT(ID__ASHBY__INTERVIEW_STAGE)) {
        const error = `Ashby - Review Stage ID not found`;
        results.errors = Arr(results.errors);
        results.errors.unshift(error);
        // ❌ Escape process if there is an error getting review stage
        throw error;
      }
      // Create application
      const ashbyApplication = Obj(
        Obj(
          await Http.post(Core.getApi('Credentials/api'), {
            provider: KEY__PROVIDER__ASHBY,
            action: 'createApplication',
            context: {
              ASHBY__CANDIDATE__ID: ID__ASHBY__CANDIDATE,
              ASHBY__JOB__ID: ID__ASHBY__JOB,
              ASHBY__SOURCE__ID: ID__ASHBY__SOURCE,
              ASHBY__USER__ID: ID__ASHBY__USER,
              API_KEY
            },
            body: {
              interviewStageId: ID__ASHBY__INTERVIEW_STAGE
            }
          })
        ).results
      );
      const ID__ASHBY__APPLICATION = trim(ashbyApplication.id);
      if (NOT(ID__ASHBY__APPLICATION)) {
        const error = `Ashby - Application creation failed`;
        results.errors = Arr(results.errors);
        results.errors.unshift(error);
        // ❌ Escape process if there is an error creating application
        throw error;
      }

      // SEND EMAIL TO ADMIN
      Core.showLoader(
        `Completing submission process`
      );
      const emailOptions = {
        ...emailParams,
        to: Core.getResumeSubmissionBcc()
      };
      emailOptions.subject = (
        `Submitted via ${CAPITALIZED__PROVIDER} API${emailOptions.subject ? ` | ${emailOptions.subject}` : ''}`
      );
      emailOptions.source = 'Ashby_lib__submit__success';
      emailOptions.key = KEY__CREDENTIAL__SECONDARY;
      emailOptions.replyTo = KEY__CREDENTIAL__PRIMARY;
      await sendSafeEmail(emailOptions);

      Core.showSuccess(
        `Submitted candidate successfully!`
      );

      // MAKE STAGE TRANSITION
      await getControllerCandidateResumeSubmission().transitionAfterSubmission({
        submissionMethodId: EMPLOYER_SUBMISSION_METHOD__ATS,
        atsTypeId: employer.atsTypeId,
        atsContextUpdate: {
          applicationId: ID__ASHBY__APPLICATION,
          candidateId: ID__ASHBY__CANDIDATE,
          jobId: ID__ASHBY__JOB
        }
      });

    }
    catch (error) {
      globalErrorHandler(error);
      const emailOptions = {
        ...emailParams,
        to: Core.getResumeSubmissionBcc()
      };
      emailOptions.subject = (
        `ERROR | ${CAPITALIZED__PROVIDER} API failed${emailOptions.subject ? ` | ${emailOptions.subject}` : ''}`
      );
      emailOptions.html = joinHTML(
        `
          --------------------------------------------------
          <br/><br/>
          Error:<br/>
          ${error}
          <br/><br/>
          --------------------------------------------------
          <br/><br/>
          ${emailOptions.html}
        `
      );
      emailOptions.html = emailOptions.html.replace(/\<\/?html\>/gi, '');
      emailOptions.html = emailOptions.html.replace(/\<\/?head\>/gi, '');
      emailOptions.html = emailOptions.html.replace(/\<\/?body\>/gi, '');
      emailOptions.source = 'Ashby_lib__submit__error';
      emailOptions.key = KEY__CREDENTIAL__SECONDARY;
      emailOptions.replyTo = KEY__CREDENTIAL__PRIMARY;
      sendSafeEmail(emailOptions);
    }
    return results;
  }
};
export default Ashby;
window.Ashby = Ashby;
